import { useCallback, useEffect, useRef, useState } from "react";
import { monaco, languageForPath } from "./monaco.ts";
import { useRelay } from "./relayContext.tsx";
import { loadTabOrder, reorderTabPaths, saveTabOrder } from "./tabOrder.ts";
import { loadEditorSettings } from "./editorSettings.ts";

export interface OpenFile {
  path: string;
  model: monaco.editor.ITextModel;
  /** Version id of the model at the last load/save; dirty when it differs. */
  savedVersionId: number;
  mtimeMs: number;
  dirty: boolean;
  saving: boolean;
  error: string | null;
}

export interface OpenFiles {
  files: OpenFile[];
  activePath: string | null;
  open: (path: string) => void;
  close: (path: string) => void;
  activate: (path: string) => void;
  save: (path?: string) => Promise<void>;
  /** Saves all dirty tabs via `save`. */
  saveAll: () => Promise<void>;
  /** Reloads all dirty tabs from disk and clears dirty state; rejects if any reload failed. */
  discardAll: () => Promise<void>;
  format: (path?: string) => Promise<{ formatted: boolean; error?: string; formatter?: string }>;
  loadError: string | null;
  /** Reorders an open tab from fromIndex to toIndex, persisting the new order. */
  reorder: (fromIndex: number, toIndex: number) => void;
  /** Close tabs at/below `path` without prompting (entry was deleted). */
  forget: (path: string) => void;
  /** Re-point tabs at/below `from` to `to` after a rename/move. */
  renamed: (from: string, to: string) => void;
  /** True if any open tab at/below `path` has unsaved changes. */
  hasDirty: (path: string) => boolean;
  /** Paths of open tabs with unsaved human edits (a ChangeSet touching them must not be applied). */
  dirtyPaths: () => string[];
  /** After an applied ChangeSet: reload clean tabs from disk, close tabs whose file is gone. Never touches dirty tabs. */
  refreshFromDisk: (paths: string[]) => Promise<void>;
  /** Opens files as tabs WITHOUT activating them (rename / code-action edits must land in a tab the user can save). */
  ensureOpen: (paths: string[]) => Promise<boolean>;
  /** Opens `path` and selects `range` once the editor shows it (Go to Definition, Problems panel). */
  reveal: (path: string, range: RevealRange) => void;
  pendingReveal: PendingReveal | null;
  clearReveal: () => void;
  /** Fires after a file was written to disk (drives textDocument/didSave). Returns an unsubscribe function. */
  onSaved: (fn: (path: string) => void) => () => void;
  selection: EditorSelectionState | null;
  setSelection: (s: EditorSelectionState | null) => void;
}

export interface RevealRange {
  startLineNumber: number;
  startColumn: number;
  endLineNumber: number;
  endColumn: number;
}
export interface PendingReveal {
  path: string;
  range: RevealRange;
  nonce: number;
}

import type { EditorSelectionState } from "./composerContext.ts";
export type { EditorSelectionState };

/**
 * Tabs, dirty state and save for one Project. Each file gets one Monaco model (URI scoped by machine +
 * Project so tabs of different Projects never collide).
 */
export function useOpenFiles(machineId: string, projectId: string): OpenFiles {
  const relay = useRelay();
  const [files, setFiles] = useState<OpenFile[]>([]);
  const [activePath, setActivePath] = useState<string | null>(null);
  const [selection, setSelection] = useState<EditorSelectionState | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [pendingReveal, setPendingReveal] = useState<PendingReveal | null>(null);
  const savedListeners = useRef(new Set<(path: string) => void>());
  const onSaved = useCallback((fn: (path: string) => void) => {
    savedListeners.current.add(fn);
    return () => void savedListeners.current.delete(fn);
  }, []);
  const filesRef = useRef(files);
  filesRef.current = files;

  const update = useCallback((path: string, patch: Partial<OpenFile>) => {
    setFiles((fs) => fs.map((f) => (f.path === path ? { ...f, ...patch } : f)));
  }, []);

  // Restore persisted tab order for this Project on reload.
  useEffect(() => {
    let cancelled = false;
    const stored = loadTabOrder(machineId, projectId);
    if (stored.length === 0) return;

    void Promise.allSettled(
      stored.map((path) =>
        relay
          .machine(machineId, "filesystem/readFile", { projectId, path })
          .then((r) => ({ path, content: r.content, mtimeMs: r.mtimeMs })),
      ),
    ).then((results) => {
      if (cancelled) return;
      const opened: OpenFile[] = [];
      for (const res of results) {
        if (res.status === "fulfilled") {
          const { path, content, mtimeMs } = res.value;
          const uri = monaco.Uri.from({
            scheme: "racler",
            authority: machineId,
            path: `/${projectId}/${path}`,
          });
          const model =
            monaco.editor.getModel(uri) ?? monaco.editor.createModel(content, languageForPath(path), uri);
          model.setValue(content);
          const file: OpenFile = {
            path,
            model,
            savedVersionId: model.getAlternativeVersionId(),
            mtimeMs,
            dirty: false,
            saving: false,
            error: null,
          };
          model.onDidChangeContent(() => {
            const current = filesRef.current.find((f) => f.path === path);
            if (current) update(path, { dirty: model.getAlternativeVersionId() !== current.savedVersionId });
          });
          opened.push(file);
        }
      }
      if (cancelled) return;
      if (opened.length > 0) {
        setFiles((existing) => {
          const existingPaths = new Set(existing.map((f) => f.path));
          const toAdd = opened.filter((f) => !existingPaths.has(f.path));
          const all = [...toAdd, ...existing];
          saveTabOrder(machineId, projectId, all.map((f) => f.path));
          return all;
        });
        setActivePath((current) => current ?? opened[0]?.path ?? null);
      } else {
        saveTabOrder(machineId, projectId, []);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [machineId, projectId, relay, update]);

  const reorder = useCallback(
    (fromIndex: number, toIndex: number) => {
      setFiles((fs) => {
        const next = reorderTabPaths(fs, fromIndex, toIndex);
        saveTabOrder(machineId, projectId, next.map((f) => f.path));
        return next;
      });
    },
    [machineId, projectId],
  );

  const open = useCallback(
    (path: string) => {
      setActivePath(path);
      if (filesRef.current.some((f) => f.path === path)) return;
      relay
        .machine(machineId, "filesystem/readFile", { projectId, path })
        .then((r) => {
          if (filesRef.current.some((f) => f.path === path)) return;
          const uri = monaco.Uri.from({
            scheme: "racler",
            authority: machineId,
            path: `/${projectId}/${path}`,
          });
          const model =
            monaco.editor.getModel(uri) ?? monaco.editor.createModel(r.content, languageForPath(path), uri);
          model.setValue(r.content);
          const file: OpenFile = {
            path,
            model,
            savedVersionId: model.getAlternativeVersionId(),
            mtimeMs: r.mtimeMs,
            dirty: false,
            saving: false,
            error: null,
          };
          model.onDidChangeContent(() => {
            const current = filesRef.current.find((f) => f.path === path);
            if (current) update(path, { dirty: model.getAlternativeVersionId() !== current.savedVersionId });
          });
          setLoadError(null);
          setFiles((fs) => {
            const next = [...fs, file];
            saveTabOrder(machineId, projectId, next.map((f) => f.path));
            return next;
          });
        })
        .catch((e: unknown) => {
          setLoadError(`${path}: ${(e as Error).message}`);
          setActivePath((p) => (p === path ? (filesRef.current.at(-1)?.path ?? null) : p));
        });
    },
    [relay, machineId, projectId, update],
  );

  const ensureOpen = useCallback(
    async (paths: string[]): Promise<boolean> => {
      const missing = [...new Set(paths)].filter((p) => !filesRef.current.some((f) => f.path === p));
      const loaded = await Promise.allSettled(
        missing.map((path) =>
          relay.machine(machineId, "filesystem/readFile", { projectId, path }).then((r) => ({ path, r })),
        ),
      );
      const added: OpenFile[] = [];
      for (const res of loaded) {
        if (res.status !== "fulfilled") return false;
        const { path, r } = res.value;
        if (filesRef.current.some((f) => f.path === path) || added.some((f) => f.path === path)) continue;
        const uri = monaco.Uri.from({ scheme: "racler", authority: machineId, path: `/${projectId}/${path}` });
        const model =
          monaco.editor.getModel(uri) ?? monaco.editor.createModel(r.content, languageForPath(path), uri);
        model.setValue(r.content);
        const file: OpenFile = {
          path,
          model,
          savedVersionId: model.getAlternativeVersionId(),
          mtimeMs: r.mtimeMs,
          dirty: false,
          saving: false,
          error: null,
        };
        model.onDidChangeContent(() => {
          const current = filesRef.current.find((f) => f.path === path);
          if (current) update(path, { dirty: model.getAlternativeVersionId() !== current.savedVersionId });
        });
        added.push(file);
      }
      if (added.length > 0) {
        // Synchronously visible to the change listeners above: an edit applied right after must mark the tab dirty.
        filesRef.current = [...filesRef.current, ...added];
        setFiles((fs) => {
          const have = new Set(fs.map((f) => f.path));
          const next = [...fs, ...added.filter((f) => !have.has(f.path))];
          saveTabOrder(machineId, projectId, next.map((f) => f.path));
          return next;
        });
      }
      return true;
    },
    [relay, machineId, projectId, update],
  );

  const reveal = useCallback(
    (path: string, range: RevealRange) => {
      setPendingReveal({ path, range, nonce: Date.now() + Math.random() });
      open(path);
    },
    [open],
  );
  const clearReveal = useCallback(() => setPendingReveal(null), []);

  const format = useCallback(
    async (path?: string): Promise<{ formatted: boolean; error?: string; formatter?: string }> => {
      const target = filesRef.current.find((f) => f.path === (path ?? activePath));
      if (!target) return { formatted: false, error: "No active file" };

      const currentContent = target.model.getValue();
      try {
        const res = await relay.machine(machineId, "editor/formatDocument", {
          projectId,
          path: target.path,
          content: currentContent,
        });

        if (res.changed && res.content !== currentContent) {
          // Apply edits via pushEditOperations to preserve undo stack and reasonable cursor state
          const fullRange = target.model.getFullModelRange();
          target.model.pushEditOperations(
            [],
            [
              {
                range: fullRange,
                text: res.content,
              },
            ],
            () => null,
          );
          update(target.path, {
            dirty: target.model.getAlternativeVersionId() !== target.savedVersionId,
            error: null,
          });
        }
        return { formatted: res.changed, formatter: res.formatter };
      } catch (err) {
        const message = (err as Error).message;
        update(target.path, { error: message });
        return { formatted: false, error: message };
      }
    },
    [relay, machineId, projectId, activePath, update],
  );

  const save = useCallback(
    async (path?: string) => {
      const target = filesRef.current.find((f) => f.path === (path ?? activePath));
      if (!target || target.saving) return;

      const settings = loadEditorSettings();
      if (settings.formatOnSave) {
        const formatRes = await format(target.path);
        if (formatRes.error) {
          // Formatting is optional, saving is mandatory: keep the buffer as is and save it anyway.
          // The error is only logged; the update below clears the error `format` set on the tab,
          // so a formatter problem is never reported as a failed save.
          console.warn(`Format on Save skipped for ${target.path}: ${formatRes.error}`);
        }
      }

      const versionAtSave = target.model.getAlternativeVersionId();
      update(target.path, { saving: true, error: null });
      try {
        const r = await relay.machine(machineId, "filesystem/writeFile", {
          projectId,
          path: target.path,
          content: target.model.getValue(),
          expectedMtimeMs: target.mtimeMs,
        });
        update(target.path, {
          saving: false,
          mtimeMs: r.mtimeMs,
          savedVersionId: versionAtSave,
          dirty: target.model.getAlternativeVersionId() !== versionAtSave,
        });
        for (const l of savedListeners.current) l(target.path);
      } catch (e) {
        update(target.path, { saving: false, error: (e as Error).message });
      }
    },
    [relay, machineId, projectId, activePath, update, format],
  );

  /** Saves every dirty tab through the single-file save path; failed tabs stay dirty with their error set. */
  const saveAll = useCallback(async () => {
    for (const path of filesRef.current.filter((f) => f.dirty).map((f) => f.path)) {
      await save(path);
    }
  }, [save]);

  /** Restores every dirty tab to its on-disk (last saved) content. Tabs that fail to reload stay dirty. */
  const discardAll = useCallback(async () => {
    let failed: string | null = null;
    for (const path of filesRef.current.filter((f) => f.dirty).map((f) => f.path)) {
      const f = filesRef.current.find((x) => x.path === path);
      if (!f) continue;
      try {
        const r = await relay.machine(machineId, "filesystem/readFile", { projectId, path });
        f.model.setValue(r.content);
        update(path, {
          mtimeMs: r.mtimeMs,
          savedVersionId: f.model.getAlternativeVersionId(),
          dirty: false,
          error: null,
        });
      } catch (e) {
        failed = `${path}: ${(e as Error).message}`;
        update(path, { error: (e as Error).message });
      }
    }
    if (failed) throw new Error(`Could not discard all changes (${failed})`);
  }, [relay, machineId, projectId, update]);

  const close = useCallback(
    (path: string) => {
      const f = filesRef.current.find((x) => x.path === path);
      if (f?.dirty && !confirm(`${path} has unsaved changes. Close anyway?`)) return;
      f?.model.dispose();
      setFiles((fs) => {
        const rest = fs.filter((x) => x.path !== path);
        setActivePath((p) => (p === path ? (rest.at(-1)?.path ?? null) : p));
        saveTabOrder(machineId, projectId, rest.map((x) => x.path));
        return rest;
      });
    },
    [machineId, projectId],
  );

  /** Tabs at `path` or below it (folder operations). */
  const affected = useCallback(
    (path: string) => filesRef.current.filter((f) => f.path === path || f.path.startsWith(`${path}/`)),
    [],
  );

  /** Drop tabs after the underlying entry was deleted or moved away (no confirmation; caller checked dirty). */
  const forget = useCallback(
    (path: string) => {
      const gone = affected(path);
      if (gone.length === 0) return;
      gone.forEach((f) => f.model.dispose());
      const goneSet = new Set(gone.map((f) => f.path));
      setFiles((fs) => {
        const rest = fs.filter((x) => !goneSet.has(x.path));
        setActivePath((p) => (p && goneSet.has(p) ? (rest.at(-1)?.path ?? null) : p));
        saveTabOrder(machineId, projectId, rest.map((x) => x.path));
        return rest;
      });
    },
    [affected, machineId, projectId],
  );

  /** After a rename/move: reopen the files that were open at their new location, preserving tab order. */
  const renamed = useCallback(
    (from: string, to: string) => {
      const wasActive = activePath !== null && (activePath === from || activePath.startsWith(`${from}/`));
      const nextActive = wasActive && activePath ? to + activePath.slice(from.length) : activePath;

      const toRename = affected(from);
      if (toRename.length === 0) return;

      const renameMap = new Map<string, string>();
      for (const f of toRename) {
        renameMap.set(f.path, to + f.path.slice(from.length));
      }

      setFiles((fs) => {
        const next = fs.map((f) => {
          const newPath = renameMap.get(f.path);
          if (!newPath) return f;
          const content = f.model.getValue();
          f.model.dispose();
          const uri = monaco.Uri.from({
            scheme: "racler",
            authority: machineId,
            path: `/${projectId}/${newPath}`,
          });
          const model = monaco.editor.createModel(content, languageForPath(newPath), uri);
          model.onDidChangeContent(() => {
            const current = filesRef.current.find((x) => x.path === newPath);
            if (current) update(newPath, { dirty: model.getAlternativeVersionId() !== current.savedVersionId });
          });
          return {
            ...f,
            path: newPath,
            model,
            savedVersionId: model.getAlternativeVersionId(),
          };
        });
        saveTabOrder(machineId, projectId, next.map((f) => f.path));
        return next;
      });
      if (nextActive) setActivePath(nextActive);
    },
    [affected, machineId, projectId, update, activePath],
  );

  const hasDirty = useCallback((path: string) => affected(path).some((f) => f.dirty), [affected]);

  const dirtyPaths = useCallback(() => filesRef.current.filter((f) => f.dirty).map((f) => f.path), []);

  const refreshFromDisk = useCallback(
    async (paths: string[]) => {
      for (const path of new Set(paths)) {
        const f = filesRef.current.find((x) => x.path === path);
        if (!f || f.dirty) continue; // human edits win: never overwrite an unsaved tab
        try {
          const r = await relay.machine(machineId, "filesystem/readFile", { projectId, path });
          f.model.setValue(r.content);
          update(path, {
            mtimeMs: r.mtimeMs,
            savedVersionId: f.model.getAlternativeVersionId(),
            dirty: false,
            error: null,
          });
        } catch {
          forget(path); // deleted or moved by the applied ChangeSet
        }
      }
    },
    [relay, machineId, projectId, update, forget],
  );

  // Dispose models when the Project view goes away.
  useEffect(() => () => filesRef.current.forEach((f) => f.model.dispose()), []);

  // Warn before leaving the page with unsaved changes.
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (filesRef.current.some((f) => f.dirty)) e.preventDefault();
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, []);

  return {
    files,
    activePath,
    open,
    close,
    activate: setActivePath,
    save,
    saveAll,
    discardAll,
    format,
    loadError,
    reorder,
    forget,
    renamed,
    hasDirty,
    dirtyPaths,
    refreshFromDisk,
    ensureOpen,
    reveal,
    pendingReveal,
    clearReveal,
    onSaved,
    selection,
    setSelection,
  };
}
